// Mixer wie in Pro Tools (Taste X): Kanalzüge mit Inserts A–J, Sends auf Hall und Delay, Ein- und Ausgang,
// Panorama, Solo/Mute, Fader mit Stereo-Pegel, Aux-Busse und Master mit Lautheitsmessung (LUFS).
// Inserts sind die eingebauten SRS-Plugins oder eure Plugins über die Bridge. Auto-Mix schreibt in dieselben Plugins.
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { InsertSlot, Track } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { PLUGINS, bussesOf, pluginDef, slotLabel } from '../audio/plugins.ts';
import { SLOT_LETTERS, MAX_INSERTS, mutChannel, mutInserts, newSlot, channelInserts } from '../state/mixer.ts';
import { dbToPos, posToDb, fmtDb, FADER_MARKS } from '../components/controls.tsx';
import { useBridge } from './Dante.tsx';
import { pluginLabel } from '../audio/bridgefx.ts';
import { PluginWindow } from './PluginWindow.tsx';
import { uid } from '../state/demo.ts';
import { AutoModeSelect, toggleMuteWrite, toggleSolo, setVolumeGrouped, touchGrouped } from './Editor.tsx';
import { addBus, deleteBus, groupMembers, groupsOfTrack, isFrozen, routeTracks } from '../edit/mixops.ts';
import { isAutomated, points as autoPoints, valueAt, staticValue } from '../edit/automation.ts';

/** Wert eines Reglers: mit Automation (Read) läuft er mit der Kurve mit, sonst der eingestellte Wert */
function useAutoValueRaw(t: Track, param: string): number {
  const auto = isAutomated(t, param) && !engine.manual.has(`${t.id}|${param}`);
  const [, force] = useState(0);
  useEffect(() => {
    if (!auto) return;
    let raf = 0, last = NaN;
    const loop = () => { const v = Math.round(valueAt(autoPoints(t, param), engine.position, param, 0) * 10) / 10; if (v !== last) { last = v; force((n) => n + 1); } raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [auto, t, param]);
  return auto ? valueAt(autoPoints(t, param), engine.position, param, 0) : staticValue(t, param);
}
const useAutoValue = (t: Track, param: string) => useAutoValueRaw(t, param);

// ---- Pegelanzeigen: eine Animationsschleife für alle Kanäle, ohne React-Neuzeichnen ----
interface MeterReg { canvas: HTMLCanvasElement; readout?: HTMLElement | null; hold: [number, number]; holdT: [number, number]; max: number; clip: boolean }
const meters = new Map<string, MeterReg>();
const lin2db = (x: number) => (x > 1e-6 ? 20 * Math.log10(x) : -120);

function useMeterLoop(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const loop = () => {
      const now = performance.now();
      for (const [id, m] of meters) {
        const c = m.canvas;
        if (!c.isConnected) { meters.delete(id); continue; }
        const dpr = devicePixelRatio || 1, W = c.clientWidth, H = c.clientHeight;
        if (c.width !== W * dpr || c.height !== H * dpr) { c.width = W * dpr; c.height = H * dpr; }
        const g = c.getContext('2d')!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.clearRect(0, 0, W, H);
        const lv = engine.stereo(id);
        const bw = (W - 2) / 2;
        for (let k = 0; k < 2; k++) {
          const db = lin2db(lv[k]);
          if (db > m.hold[k] || now - m.holdT[k] > 1500) { m.hold[k] = db; m.holdT[k] = now; }
          if (db > m.max) m.max = db;
          if (lv[k] >= 1) m.clip = true;
          const x = k * (bw + 2), pos = dbToPos(Math.max(-96, db));
          g.fillStyle = '#eef1f6'; g.fillRect(x, 0, bw, H);
          const grad = g.createLinearGradient(0, H, 0, 0);
          grad.addColorStop(0, '#2f5fd0'); grad.addColorStop(dbToPos(-12), '#2f5fd0'); grad.addColorStop(dbToPos(-6), '#c7861b'); grad.addColorStop(dbToPos(-0.5), '#e23e2c'); grad.addColorStop(1, '#e23e2c');
          g.fillStyle = grad; g.fillRect(x, H - pos * H, bw, pos * H);
          const hp = dbToPos(Math.max(-96, m.hold[k]));
          if (hp > 0.01) { g.fillStyle = m.hold[k] > -0.1 ? '#e23e2c' : '#071026'; g.fillRect(x, H - hp * H, bw, 1.5); }
        }
        if (m.readout) { m.readout.textContent = m.max > -95 ? fmtDb(m.max) : '−∞'; m.readout.classList.toggle('clip', m.clip || m.max > -0.05); }
      }
      // Gain-Reduction der Dynamik-Plugins in den Insert-Slots
      document.querySelectorAll<HTMLElement>('[data-gr]').forEach((el) => {
        const [ch, slot] = el.dataset.gr!.split('|');
        const gr = engine.plugin(ch, slot)?.meter?.gr ?? 0;
        el.style.width = `${Math.min(100, (-gr / 18) * 100)}%`;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);
}

function MeterCanvas({ id, readout }: { id: string; readout?: React.RefObject<HTMLElement | null> }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    meters.set(id, { canvas: c, readout: readout?.current, hold: [-120, -120], holdT: [0, 0], max: -120, clip: false });
    return () => { if (meters.get(id)?.canvas === c) meters.delete(id); };
  }, [id, readout]);
  return <canvas ref={ref} className="mx-meter" title="Pegel L/R. Klick setzt die Spitzenanzeige zurück" onClick={() => { const m = meters.get(id); if (m) { m.max = -120; m.clip = false; } }} />;
}

// ---- Fader ----
function Fader({ db, onChange, onTouch }: { db: number; onChange: (db: number) => void; onTouch?: (on: boolean) => void }) {
  const drag = useRef<{ y: number; pos: number; h: number } | null>(null);
  const pos = dbToPos(db);
  return (
    <div className="mx-fader" title="Ziehen, ⇧ für fein, Doppelklick oder ⌥-Klick = 0 dB"
      onPointerDown={(e) => {
        if (e.altKey) { onChange(0); return; }
        const r = (e.currentTarget as HTMLElement).querySelector('.mx-track')!.getBoundingClientRect();
        drag.current = { y: e.clientY, pos, h: r.height };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        onTouch?.(true);
      }}
      onPointerMove={(e) => { const d = drag.current; if (!d) return; const np = d.pos + ((d.y - e.clientY) / d.h) * (e.shiftKey ? 0.12 : 1); onChange(Math.round(posToDb(np) * 10) / 10); }}
      onPointerUp={() => { drag.current = null; onTouch?.(false); }}
      onDoubleClick={() => onChange(0)}>
      <div className="mx-track">
        {FADER_MARKS.map((m) => <span key={m} className="mk" style={{ bottom: `${dbToPos(m) * 100}%` }}>{m > 0 ? `+${m}` : m === 0 ? '0' : m}</span>)}
        <div className="mx-slot" />
        <div className="mx-cap" style={{ bottom: `${pos * 100}%` }} />
      </div>
    </div>
  );
}

// ---- Panorama ----
function Pan({ value, onChange, onTouch }: { value: number; onChange: (v: number) => void; onTouch?: (on: boolean) => void }) {
  const drag = useRef<{ y: number; v: number } | null>(null);
  const a = value * 135, r = 13, c = 16;
  const rad = ((a - 90) * Math.PI) / 180;
  const label = Math.abs(value) < 0.005 ? '<0>' : value < 0 ? `<${Math.round(-value * 100)}` : `${Math.round(value * 100)}>`;
  return (
    <div className="mx-pan" title="Panorama. Ziehen, Doppelklick = Mitte"
      onPointerDown={(e) => { drag.current = { y: e.clientY, v: value }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); onTouch?.(true); }}
      onPointerMove={(e) => { const d = drag.current; if (!d) return; onChange(Math.max(-1, Math.min(1, Math.round((d.v + ((d.y - e.clientY) / 150) * (e.shiftKey ? 0.15 : 1)) * 100) / 100))); }}
      onPointerUp={() => { drag.current = null; onTouch?.(false); }}
      onDoubleClick={() => onChange(0)}>
      <svg width={32} height={32}>
        <circle cx={c} cy={c} r={r} fill="#fff" stroke="#dfe3ea" strokeWidth={2} />
        <line x1={c} y1={c} x2={c + r * Math.cos(rad)} y2={c + r * Math.sin(rad)} stroke="#003399" strokeWidth={2.2} strokeLinecap="round" />
      </svg>
      <span className="mono">{label}</span>
    </div>
  );
}

// ---- Plugin-Auswahl ----
export type Menu = { channel: string; index: number; x: number; y: number; replace?: string; actions?: boolean } | null;

export function PluginMenu({ menu, close }: { menu: NonNullable<Menu>; close: () => void }) {
  const commit = useStudio((s) => s.commit);
  const openPlugin = useStudio((s) => s.openPlugin);
  const bridge = useBridge();
  const slot = menu.replace ? channelInserts(useStudio.getState().project, menu.channel).find((s) => s.id === menu.replace) : undefined;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const off = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) close(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); close(); } };
    window.addEventListener('pointerdown', off, true); window.addEventListener('keydown', esc, true);
    return () => { window.removeEventListener('pointerdown', off, true); window.removeEventListener('keydown', esc, true); };
  }, [close]);
  const choose = (pluginId: string, name: string) => {
    const s = newSlot(pluginId);
    if (pluginId.startsWith('bridge:')) { const b = bridge.plugins.find((x) => `bridge:${x.id}` === pluginId); s.label = b ? pluginLabel(b.vendor, b.name) : name; }
    commit(menu.replace ? `Insert ersetzen: ${name}` : `Insert ${name}`, (p) => {
      const list = mutInserts(p, menu.channel);
      if (menu.replace) { const i = list.findIndex((x) => x.id === menu.replace); if (i >= 0) list[i] = s; }
      else list.splice(Math.min(menu.index, list.length), 0, s);
    });
    openPlugin({ channel: menu.channel, slotId: s.id });
    close();
  };
  const left = Math.min(menu.x, window.innerWidth - 250), top = Math.min(menu.y, window.innerHeight - 420);
  if (menu.actions && slot) return (
    <div className="mx-menu" ref={ref} style={{ left, top }}>
      <div className="mh">{slotLabel(slot)}</div>
      <button onClick={() => { openPlugin({ channel: menu.channel, slotId: slot.id }); close(); }}>Öffnen</button>
      <button onClick={() => { commit(slot.bypass ? 'Insert aktiv' : 'Insert Bypass', (p) => { const s = mutInserts(p, menu.channel).find((x) => x.id === slot.id); if (s) s.bypass = !s.bypass; }); close(); }}>{slot.bypass ? 'Bypass aus' : 'Bypass'}</button>
      <button onClick={() => { commit('Insert duplizieren', (p) => { const l = mutInserts(p, menu.channel); const i = l.findIndex((x) => x.id === slot.id); if (l.length < MAX_INSERTS) l.splice(i + 1, 0, { ...structuredClone(slot), id: uid('ins'), ai: false }); }); close(); }}>Duplizieren</button>
      <PluginList onPick={choose} bridge={bridge.plugins} title="Ersetzen durch" />
      <button className="danger" onClick={() => { commit('Insert entfernen', (p) => { const l = mutInserts(p, menu.channel); const i = l.findIndex((x) => x.id === slot.id); if (i >= 0) l.splice(i, 1); }); const w = useStudio.getState().pluginWin; if (w?.slotId === slot.id) openPlugin(null); close(); }}>Entfernen</button>
    </div>
  );
  return <div className="mx-menu" ref={ref} style={{ left, top }}><PluginList onPick={choose} bridge={bridge.plugins} title={`Insert ${SLOT_LETTERS[menu.index]}`} /></div>;
}

function PluginList({ onPick, bridge, title }: { onPick: (id: string, name: string) => void; bridge: { id: string; name: string; vendor: string; isInstrument: boolean }[]; title: string }) {
  const groups = ['EQ', 'Dynamik', 'Farbe', 'Raum', 'Werkzeug'] as const;
  const own = bridge.filter((b) => !b.isInstrument);
  return (
    <>
      <div className="mh">{title}</div>
      {groups.map((g) => (
        <div key={g} className="mg"><small>{g}</small>{PLUGINS.filter((p) => p.group === g).map((p) => <button key={p.id} onClick={() => onPick(p.id, p.name)}>{p.name}</button>)}</div>
      ))}
      <div className="mg"><small>Eure Plugins (Bridge)</small>
        {own.length ? own.map((b) => <button key={b.id} onClick={() => onPick(`bridge:${b.id}`, b.name)} title="Rechnet auf dem Studio-Rechner über die Bridge">{b.vendor} {b.name}</button>)
          : <span className="t2" style={{ fontSize: 11.5, padding: '2px 10px', display: 'block' }}>Bridge nicht verbunden</span>}
      </div>
    </>
  );
}

// ---- Kanalzug ----
type Kind = 'track' | 'bus';
const Strip = memo(function Strip({ ch, kind, offset, busses, setMenu, sel }: { ch: Track; kind: Kind; offset: number; busses: Track[]; setMenu: (m: Menu) => void; sel: boolean }) {
  const commit = useStudio((s) => s.commit);
  const openPlugin = useStudio((s) => s.openPlugin);
  const win = useStudio((s) => s.pluginWin);
  const bridge = useBridge();
  const readout = useRef<HTMLSpanElement>(null);
  const upd = (label: string, f: (t: Track) => void, merge = false) => commit(label, (p) => { const t = mutChannel(p, ch.id); if (t) f(t); }, { merge });
  const io = kind === 'bus' ? 'Bus' : ch.kind === 'midi' ? (ch.instrument?.plugin.startsWith('bridge:') ? ch.instrument.plugin.slice(7) : `Instr. ${ch.instrument?.preset ?? 'Drums'}`) : ch.input?.kind === 'bridge' ? (ch.input.label ?? `Dante ${ch.input.channel + 1}`) : ch.input?.kind === 'remote' ? 'Remote' : 'Browser';
  const touch = (param: string) => (on: boolean) => { if (kind !== 'track') return; if (param === 'volume') touchGrouped(ch, param, on); else engine.touch(ch.id, param, on); };
  const project = useStudio((s) => s.project);
  const groups = kind === 'track' ? groupsOfTrack(project, ch.id) : [];
  const [renaming, setRenaming] = useState(false);
  const vol = useAutoValue(ch, 'volume'), pan = useAutoValue(ch, 'pan');
  const muteV = useAutoValueRaw(ch, 'mute');
  const muted = kind === 'track' && isAutomated(ch, 'mute') ? muteV >= 0.5 : ch.mute;
  const bridgeName = (s: InsertSlot) => s.plugin.startsWith('bridge:') ? bridge.plugins.find((b) => `bridge:${b.id}` === s.plugin)?.name ?? s.label ?? 'Plugin' : undefined;

  const onDrop = (e: React.DragEvent, index: number) => {
    const raw = e.dataTransfer.getData('application/x-srs-insert');
    if (!raw) return;
    e.preventDefault();
    const { from, slotId } = JSON.parse(raw) as { from: string; slotId: string };
    const copy = e.altKey;
    commit(copy ? 'Insert kopieren' : 'Insert verschieben', (p) => {
      const src = mutInserts(p, from), i = src.findIndex((x) => x.id === slotId);
      if (i < 0) return;
      const dst = mutInserts(p, ch.id);
      if (dst.length >= MAX_INSERTS && (copy || from !== ch.id)) return;
      const item = copy ? { ...structuredClone(src[i]), id: uid('ins') } : src.splice(i, 1)[0];
      let at = index;
      if (!copy && from === ch.id && i < index) at--;
      dst.splice(Math.max(0, Math.min(at, dst.length)), 0, item);
    });
  };

  return (
    <div className={`mx-strip ${kind} ${sel ? 'sel' : ''}`} onClick={() => kind === 'track' && useStudio.getState().select([], ch.id)}>
      <div className="mx-sec ins-sec">
        {Array.from({ length: 5 }, (_, k) => {
          const index = offset + k, s = ch.inserts[index], open = !!s && win?.slotId === s.id;
          const def = s ? pluginDef(s.plugin) : undefined;
          return (
            <div key={k} className={`ins ${s ? 'filled' : ''} ${s?.bypass ? 'byp' : ''} ${s?.ai ? 'ai' : ''} ${open ? 'open' : ''} ${def?.group === 'Bridge' ? 'bridge' : ''}`}
              draggable={!!s}
              onDragStart={(e) => { e.dataTransfer.setData('application/x-srs-insert', JSON.stringify({ from: ch.id, slotId: s!.id })); e.dataTransfer.effectAllowed = 'copyMove'; }}
              onDragOver={(e) => { if ([...e.dataTransfer.types].includes('application/x-srs-insert')) { e.preventDefault(); e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move'; } }}
              onDrop={(e) => onDrop(e, index)}
              onClick={(e) => { e.stopPropagation(); if (s) openPlugin(open ? null : { channel: ch.id, slotId: s.id }); else if (index <= ch.inserts.length && ch.inserts.length < MAX_INSERTS) setMenu({ channel: ch.id, index: ch.inserts.length, x: e.clientX, y: e.clientY }); }}
              onContextMenu={(e) => { e.preventDefault(); if (s) setMenu({ channel: ch.id, index, x: e.clientX, y: e.clientY, replace: s.id, actions: true }); }}
              title={s ? `${SLOT_LETTERS[index]}: ${bridgeName(s) ?? slotLabel(s)}${s.ai ? ' (von der KI gesetzt)' : ''}. Klick öffnet, Rechtsklick für mehr, ziehen zum Verschieben (⌥ kopiert)` : `Insert ${SLOT_LETTERS[index]}`}>
              <span className="l">{SLOT_LETTERS[index]}</span>
              <span className="n">{s ? bridgeName(s) ?? slotLabel(s) : index === ch.inserts.length ? '+' : ''}</span>
              {s?.ai && <span className="k">KI</span>}
              {s && def?.group === 'Dynamik' && <i className="gr" data-gr={`${ch.id}|${s.id}`} />}
              {s && <button className="pw" title={s.bypass ? 'Bypass aus' : 'Bypass'} onClick={(e) => { e.stopPropagation(); commit(s.bypass ? 'Insert aktiv' : 'Insert Bypass', (p) => { const x = mutInserts(p, ch.id).find((y) => y.id === s.id); if (x) x.bypass = !x.bypass; }); }} />}
            </div>
          );
        })}
      </div>
      <div className="mx-sec send-sec">
        {busses.map((b) => {
          const lvl = kind === 'track' ? ch.sends.find((x) => x.busId === b.id)?.level ?? 0 : 0;
          const db = lvl > 0 ? 20 * Math.log10(lvl) : -96;
          return kind === 'track' ? (
            <SendBar key={b.id} name={b.name} db={isAutomated(ch, `send:${b.id}`) ? valueAt(autoPoints(ch, `send:${b.id}`), engine.position, `send:${b.id}`, db) : db} onTouch={touch(`send:${b.id}`)} onChange={(d) => upd(`Send ${b.name}`, (t) => { const v = d <= -60 ? 0 : Math.pow(10, d / 20); const x = t.sends.find((y) => y.busId === b.id); if (x) x.level = v; else t.sends.push({ busId: b.id, level: v }); }, true)} />
          ) : <div key={b.id} className="send blank" />;
        })}
      </div>
      <div className="mx-sec io-sec"><div className="io" title="Eingang">{io}</div>{kind === 'track'
          ? <select className="io out" title="Ausgang: Master oder ein Aux-Bus (Submix)" value={ch.output ?? ''} onClick={(e) => e.stopPropagation()} onChange={(e) => commit(e.target.value ? `Ausgang ${busses.find((b) => b.id === e.target.value)?.name}` : 'Ausgang Master', (p) => routeTracks(p, groupMembers(p, ch.id, 'mix'), e.target.value || undefined))}>
              <option value="">Master</option>{busses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          : <div className="io out" title="Ausgang">Master</div>}{kind === 'track' ? <AutoModeSelect t={ch} small /> : <div className="automode-ph" />}</div>
      <Pan value={pan} onTouch={touch('pan')} onChange={(v) => upd('Pan', (t) => { t.pan = v; }, true)} />
      <div className="mx-btns">
        {kind === 'track' && ch.kind === 'audio' && <button className={`rsm r ${ch.armed ? 'on' : ''}`} title="Aufnahmebereit" onClick={(e) => { e.stopPropagation(); upd('Scharf schalten', (t) => { t.armed = !t.armed; }); }}>R</button>}
        {kind === 'track' && <button className={`rsm s ${ch.solo ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); toggleSolo(ch); }}>S</button>}
        <button className={`rsm m ${muted ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); if (kind === 'track') toggleMuteWrite(ch); else upd('Mute', (t) => { t.mute = !t.mute; }); }}>M</button>
      </div>
      <div className="mx-fz">
        <Fader db={vol} onTouch={touch('volume')} onChange={(d) => { if (kind === 'track') setVolumeGrouped(ch, d); else upd('Fader', (t) => { t.volumeDb = d; }, true); }} />
        <MeterCanvas id={ch.id} readout={readout} />
      </div>
      <div className="mx-read"><span className="mono" title="Fader">{fmtDb(vol)}</span><span className="mono pk" ref={readout} title="Spitze seit Start, Klick auf die Anzeige setzt zurück">−∞</span></div>
      <div className="mx-name" style={{ borderTopColor: ch.color }}
        onDoubleClick={() => kind === 'bus' && setRenaming(true)}
        onContextMenu={(e) => { if (kind !== 'bus') return; e.preventDefault(); if (window.confirm?.(`Bus „${ch.name}“ löschen? Sends dorthin entfallen, Spuren gehen wieder auf den Master.`) ?? true) commit(`Bus „${ch.name}“ löschen`, (p) => deleteBus(p, ch.id)); }}
        title={kind === 'bus' ? 'Doppelklick: umbenennen · Rechtsklick: Bus löschen' : ch.name}>
        {renaming ? <input autoFocus defaultValue={ch.name} onBlur={(e) => { setRenaming(false); const v = e.target.value.trim(); if (v && v !== ch.name) upd('Bus umbenennen', (t) => { t.name = v; }); }} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenaming(false); }} />
          : <b>{ch.name}</b>}
        <small>{kind === 'bus' ? 'Aux' : ch.kind === 'midi' ? 'MIDI' : 'Audio'}{ch.frozen && isFrozen(project, ch) ? ' · ❄' : ''}</small>
        {groups.length > 0 && <span className="mx-grp">{groups.map((g) => <i key={g.id} style={{ background: g.color }} title={`Gruppe ${g.name}${g.active ? '' : ' (aus)'}`}>{g.name.slice(0, 1)}</i>)}</span>}
      </div>
    </div>
  );
});

function SendBar({ name, db, onChange, onTouch }: { name: string; db: number; onChange: (db: number) => void; onTouch?: (on: boolean) => void }) {
  const drag = useRef<{ x: number; pos: number; w: number } | null>(null);
  const pos = db <= -60 ? 0 : dbToPos(db);
  return (
    <div className="send" title={`Send ${name}: ${db <= -60 ? 'aus' : `${fmtDb(db)} dB`}. Ziehen, Doppelklick = aus`}
      onPointerDown={(e) => { e.stopPropagation(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); drag.current = { x: e.clientX, pos, w: r.width }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); onTouch?.(true); }}
      onPointerMove={(e) => { const d = drag.current; if (!d) return; const np = d.pos + ((e.clientX - d.x) / d.w) * (e.shiftKey ? 0.15 : 1); onChange(np <= 0.01 ? -96 : Math.round(posToDb(np) * 10) / 10); }}
      onPointerUp={() => { drag.current = null; onTouch?.(false); }}
      onDoubleClick={() => onChange(-96)}>
      <i style={{ width: `${pos * 100}%` }} />
      <span className="sn">{name}</span><span className="sv">{db <= -60 ? '' : fmtDb(db)}</span>
    </div>
  );
}

function MasterStrip({ offset, setMenu }: { offset: number; setMenu: (m: Menu) => void }) {
  const masterDb = useStudio((s) => s.project.masterDb);
  const inserts = useStudio((s) => s.project.masterInserts) ?? [];
  const masterAuto = useStudio((s) => s.project.masterAutomation);
  const commit = useStudio((s) => s.commit);
  const openPlugin = useStudio((s) => s.openPlugin);
  const win = useStudio((s) => s.pluginWin);
  const readout = useRef<HTMLSpanElement>(null);
  const lufs = useRef<HTMLDivElement>(null);
  const [mon, setMon] = useState(engine.monitor);
  useEffect(() => {
    let raf = 0;
    const f = (x: number) => (Number.isFinite(x) && x > -70 ? x.toFixed(1).replace('.', ',') : '–');
    const loop = () => {
      const l = engine.loudness;
      if (lufs.current && l) {
        const v = lufs.current.querySelectorAll('b');
        v[0].textContent = f(l.m); v[1].textContent = f(l.s); v[2].textContent = f(l.i); v[3].textContent = Number.isFinite(l.maxPeak) && l.maxPeak > -90 ? fmtDb(l.maxPeak) : '–';
        v[3].classList.toggle('clip', l.maxPeak > -1);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const ch: Track = useMemo(() => ({ id: 'master', name: 'Master', color: '#071026', kind: 'bus', armed: false, mute: false, solo: false, volumeDb: masterDb, pan: 0, inserts, sends: [], automation: masterAuto }), [masterDb, inserts, masterAuto]);
  const vol = useAutoValue(ch, 'volume');
  return (
    <div className="mx-strip master">
      <div className="mx-sec ins-sec">
        {Array.from({ length: 5 }, (_, k) => {
          const index = offset + k, s = ch.inserts[index], open = !!s && win?.slotId === s.id;
          const def = s ? pluginDef(s.plugin) : undefined;
          return (
            <div key={k} className={`ins ${s ? 'filled' : ''} ${s?.bypass ? 'byp' : ''} ${s?.ai ? 'ai' : ''} ${open ? 'open' : ''}`}
              onClick={(e) => { if (s) openPlugin(open ? null : { channel: 'master', slotId: s.id }); else if (ch.inserts.length < MAX_INSERTS) setMenu({ channel: 'master', index: ch.inserts.length, x: e.clientX - 200, y: e.clientY }); }}
              onContextMenu={(e) => { e.preventDefault(); if (s) setMenu({ channel: 'master', index, x: e.clientX - 200, y: e.clientY, replace: s.id, actions: true }); }}>
              <span className="l">{SLOT_LETTERS[index]}</span>
              <span className="n">{s ? slotLabel(s) : index === ch.inserts.length ? '+' : ''}</span>
              {s?.ai && <span className="k">KI</span>}
              {s && def?.group === 'Dynamik' && <i className="gr" data-gr={`master|${s.id}`} />}
              {s && <button className="pw" title={s.bypass ? 'Bypass aus' : 'Bypass'} onClick={(e) => { e.stopPropagation(); commit(s.bypass ? 'Insert aktiv' : 'Insert Bypass', (p) => { const x = mutInserts(p, 'master').find((y) => y.id === s.id); if (x) x.bypass = !x.bypass; }); }} />}
            </div>
          );
        })}
      </div>
      <div className="mx-lufs" ref={lufs} title="Lautheit nach EBU R128. Wird bei jedem Start der Wiedergabe neu gemessen.">
        <div><small>Momentan</small><b>–</b></div>
        <div><small>Kurzzeit</small><b>–</b></div>
        <div className="big"><small>Integriert LUFS</small><b>–</b></div>
        <div><small>Spitze dBFS</small><b>–</b></div>
      </div>
      <div className="mx-mauto"><AutoModeSelect t={ch} small /></div>
      <div className="mx-btns">
        <button className={`btn small ${mon.mono ? 'on' : ''}`} title="Mono abhören" onClick={() => { const m = { ...mon, mono: !mon.mono }; setMon(m); engine.setMonitor(m); }}>Mono</button>
        <button className={`btn small ${mon.phone ? 'on' : ''}`} title="Wie auf einem Handy-Lautsprecher abhören" onClick={() => { const m = { ...mon, phone: !mon.phone }; setMon(m); engine.setMonitor(m); }}>Handy</button>
      </div>
      <div className="mx-fz">
        <Fader db={vol} onTouch={(on) => engine.touch('master', 'volume', on)} onChange={(d) => commit('Master', (p) => { p.masterDb = d; }, { merge: true })} />
        <MeterCanvas id="master" readout={readout} />
      </div>
      <div className="mx-read"><span className="mono">{fmtDb(vol)}</span><span className="mono pk" ref={readout}>−∞</span></div>
      <div className="mx-name" style={{ borderTopColor: '#071026' }}><b>Master</b><small>Stereo-Ausgang</small></div>
    </div>
  );
}

export function Mixer({ embedded = false }: { embedded?: boolean }) {
  const tracks = useStudio((s) => s.project.tracks);
  const projectBusses = useStudio((s) => s.project.busses);
  const masterInserts = useStudio((s) => s.project.masterInserts);
  const selected = useStudio((s) => s.selection.trackId);
  const win = useStudio((s) => s.pluginWin);
  const [bank, setBank] = useState(0);
  const [menu, setMenu] = useState<Menu>(null);
  const busses = bussesOf({ busses: projectBusses });
  const list = tracks.filter((t) => t.kind !== 'bus' && !t.hidden);
  const aiCount = [...list, ...busses].reduce((n, t) => n + t.inserts.filter((s) => s.ai).length, 0) + (masterInserts ?? []).filter((s) => s.ai).length;
  useMeterLoop(true);
  useEffect(() => { void engine.ensure(); }, []);
  const offset = bank * 5;
  return (
    <section className={`panel mixer ${embedded ? 'embedded' : ''}`}>
      <div className="mx-head">
        <h2>Mixer</h2>
        <span className="t2">{list.length} Spuren · {busses.length} Busse · Master</span>
        <div className="seg small" title="Insert-Bank">
          <button className={bank === 0 ? 'on' : ''} onClick={() => setBank(0)}>Inserts A–E</button>
          <button className={bank === 1 ? 'on' : ''} onClick={() => setBank(1)}>F–J</button>
        </div>
        <button className="btn small" title="Neuen Aux-Bus anlegen (z. B. Drums-Submix, zweiter Hall)" onClick={() => useStudio.getState().commit('Aux-Bus', (p) => { addBus(p); })}>+ Aux</button>
        {aiCount > 0 && <span className="pill violet" title="Von der KI gesetzte Inserts. Sobald ihr etwas daran ändert, verschwindet die Kennzeichnung.">{aiCount} Inserts von der KI</span>}
        <div className="r">
          <button className="btn small" onClick={() => { useStudio.getState().setView('mix'); useStudio.getState().setMixerOpen(false); }} title="Die KI schlägt in 14 Schritten vor, ihr entscheidet. Ergebnisse landen in diesen Plugins.">Auto-Mix</button>
          <button className="btn small" title="Spitzenanzeigen zurücksetzen" onClick={() => { for (const m of meters.values()) { m.max = -120; m.clip = false; } engine.resetLoudness(); }}>Spitzen zurücksetzen</button>
          {!embedded && <button className="btn small" onClick={() => useStudio.getState().setMixerOpen(false)} title="Taste X">Schließen</button>}
        </div>
      </div>
      <div className="mx-body" onScroll={() => setMenu(null)}>
        <div className="mx-strips">
          {list.map((t) => <Strip key={t.id} ch={t} kind="track" offset={offset} busses={busses} setMenu={setMenu} sel={selected === t.id} />)}
          <div className="mx-gap" />
          {busses.map((b) => <Strip key={b.id} ch={b} kind="bus" offset={offset} busses={busses} setMenu={setMenu} sel={false} />)}
        </div>
        <MasterStrip offset={offset} setMenu={setMenu} />
      </div>
      {menu && <PluginMenu menu={menu} close={() => setMenu(null)} />}
      {win && <PluginWindow key={win.slotId} />}
      {list.length === 0 && <div className="empty">Noch keine Spuren. In der Session mit „+ Audio“ oder „+ MIDI“ anlegen.</div>}
      {!embedded && <div className="mx-hint">X schließt den Mixer · Klick auf einen leeren Slot fügt ein Plugin ein · Rechtsklick auf ein Plugin für Bypass, Ersetzen, Entfernen · Plugins zwischen Slots ziehen (⌥ kopiert)</div>}
    </section>
  );
}

export function MixerOverlay() {
  const open = useStudio((s) => s.mixerOpen);
  if (!open) return null;
  return <div className="mixer-overlay"><Mixer /></div>;
}

